/**
 * Centralized analytics event schema for Punto Hispano.
 *
 * Standard shape: { category, action, label, ...params }
 *
 *  - category: Top-level grouping (form, widget, chat, ecommerce, navigation, blog, performance)
 *  - action:   What happened in snake_case (submit_success, click, open, view_item)
 *  - label:    Human-readable identifier of the specific element (form_id, cta_id...)
 *  - params:   Any extra GA4 params (value, currency, items, service_area...)
 *
 * The same payload is dispatched to:
 *   1. GA4 via gtag('event', action, params)
 *   2. GTM dataLayer (event = `${category}_${action}` for unique triggers + fields)
 *   3. window CustomEvent 'ph:analytics' for in-app listeners
 */

export type EventCategory =
  | "form"
  | "widget"
  | "chat"
  | "ecommerce"
  | "navigation"
  | "blog"
  | "performance"
  | "engagement";

export interface AnalyticsEvent {
  category: EventCategory;
  action: string;
  label?: string;
  params?: Record<string, unknown>;
}

type WindowWithAnalytics = Window & {
  gtag?: (...args: unknown[]) => void;
  dataLayer?: unknown[];
};

export function trackEvent({ category, action, label, params = {} }: AnalyticsEvent) {
  if (typeof window === "undefined") return;
  const w = window as WindowWithAnalytics;

  const enriched: Record<string, unknown> = {
    event_category: category,
    event_label: label,
    page_path: window.location.pathname,
    page_location: window.location.href,
    ...params,
  };

  try {
    if (typeof w.gtag === "function") {
      w.gtag("event", action, enriched);
    }

    if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({
        event: `${category}_${action}`,
        analytics_category: category,
        analytics_action: action,
        analytics_label: label,
        ...enriched,
      });
    }

    window.dispatchEvent(
      new CustomEvent("ph:analytics", {
        detail: { category, action, label, ...enriched },
      })
    );
  } catch (err) {
    console.warn("[analytics] trackEvent failed", err);
  }
}

// ============================================================
// Domain helpers (typed wrappers — preferred over raw trackEvent)
// ============================================================

export const analytics = {
  // --- Forms ---
  formSubmitSuccess(label: string, params: Record<string, unknown> = {}) {
    trackEvent({ category: "form", action: "submit_success", label, params });
    // Also fire GA4 standard generate_lead for conversion goals
    if (typeof window !== "undefined") {
      const w = window as WindowWithAnalytics;
      if (typeof w.gtag === "function") {
        w.gtag("event", "generate_lead", { currency: "CHF", value: 0, form_id: label, ...params });
      }
    }
  },
  formSubmitError(label: string, errorMessage?: string) {
    trackEvent({
      category: "form",
      action: "submit_error",
      label,
      params: { error_message: errorMessage },
    });
  },

  // --- Widgets (SmartHelpWidget, popups, modals) ---
  widgetOpen(label: string, params: Record<string, unknown> = {}) {
    trackEvent({ category: "widget", action: "open", label, params });
  },
  widgetClose(label: string, params: Record<string, unknown> = {}) {
    trackEvent({ category: "widget", action: "close", label, params });
  },
  widgetInteract(label: string, action: string, params: Record<string, unknown> = {}) {
    trackEvent({ category: "widget", action, label, params });
  },

  // --- Chat ---
  chatOpen(label: string, params: Record<string, unknown> = {}) {
    trackEvent({ category: "chat", action: "open", label, params });
  },
  chatMessageSent(label: string, params: Record<string, unknown> = {}) {
    trackEvent({ category: "chat", action: "message_sent", label, params });
  },

  // --- Navigation ---
  ctaClick(label: string, params: Record<string, unknown> = {}) {
    trackEvent({ category: "navigation", action: "cta_click", label, params });
  },

  // --- Blog ---
  blogView(label: string, params: Record<string, unknown> = {}) {
    trackEvent({ category: "blog", action: "view", label, params });
  },
  blogScroll(label: string, depth: number) {
    trackEvent({ category: "blog", action: "scroll_depth", label, params: { scroll_depth: depth } });
  },
  blogInteraction(label: string, interaction: string, params: Record<string, unknown> = {}) {
    trackEvent({ category: "blog", action: interaction, label, params });
  },

  // --- Ecommerce ---
  ecommerce(action: string, label: string, params: Record<string, unknown> = {}) {
    trackEvent({ category: "ecommerce", action, label, params });
  },

  // --- Performance ---
  webVital(name: string, value: number, params: Record<string, unknown> = {}) {
    trackEvent({
      category: "performance",
      action: "web_vital",
      label: name,
      params: { value, ...params },
    });
  },
};
